import React, { useState } from 'react';
import type { ContainerStatus, BatchUpgradeProgress } from '../types';

interface BatchUpgradeModalProps {
  containers: ContainerStatus[];
  availableVersions: string[];
  onClose: () => void;
  onStart: (tenantIds: string[], targetVersion: string, parallelLimit: number) => Promise<void>;
  onCancel: () => Promise<void>;
  progress: BatchUpgradeProgress | null;
  isRunning: boolean;
}

export const BatchUpgradeModal: React.FC<BatchUpgradeModalProps> = ({
  containers,
  availableVersions,
  onClose,
  onStart,
  onCancel,
  progress,
  isRunning,
}) => {
  const [targetVersion, setTargetVersion] = useState('');
  const [parallelLimit, setParallelLimit] = useState(3);
  const [selectedTenants, setSelectedTenants] = useState<Set<string>>(new Set());
  const [starting, setStarting] = useState(false);

  const uniqueTenants = Array.from(
    new Map(containers.map((c) => [c.tenant_id, c])).values()
  );

  const toggleTenant = (id: string) => {
    const next = new Set(selectedTenants);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedTenants(next);
  };

  const allSelected = selectedTenants.size === uniqueTenants.length && uniqueTenants.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedTenants(new Set());
    } else {
      setSelectedTenants(new Set(uniqueTenants.map((t) => t.tenant_id)));
    }
  };

  const handleStart = async () => {
    if (!targetVersion || selectedTenants.size === 0) return;
    setStarting(true);
    try {
      await onStart(Array.from(selectedTenants), targetVersion, parallelLimit);
    } finally {
      setStarting(false);
    }
  };

  const progressPercent =
    progress && progress.total > 0
      ? Math.round(((progress.completed + progress.failed + (progress.skipped || 0)) / progress.total) * 100)
      : 0;

  const isComplete = progress != null && !isRunning && progressPercent === 100;
  const allSucceeded = isComplete && progress.failed === 0;
  const skippedTenants = progress?.details?.filter((d) => d.skipped) ?? [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: '600px', width: '90%' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Batch upgrade"
      >
        <div className="modal-header">
          <h2 style={{ fontSize: '16px' }}>Batch Upgrade</h2>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="modal-body" style={{ padding: '20px' }}>
          {/* Completion state */}
          {isComplete && progress ? (
            <div aria-live="polite" style={{ textAlign: 'center', padding: '24px 0' }}>
              {allSucceeded ? (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>&#10003;</div>
                  <h3 style={{ marginBottom: '8px' }}>Upgrade Complete</h3>
                  <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '16px' }}>
                    {progress.completed} tenant{progress.completed !== 1 ? 's' : ''} upgraded to v{targetVersion}
                    {(progress.skipped || 0) > 0 && ` (${progress.skipped} already on target)`}
                  </p>
                </>
              ) : (
                <>
                  <h3 style={{ marginBottom: '8px' }}>Upgrade Finished</h3>
                  <p style={{ fontSize: '13px', marginBottom: '16px' }}>
                    {progress.completed} succeeded, {progress.failed} failed
                    {(progress.skipped || 0) > 0 && `, ${progress.skipped} skipped`}
                  </p>
                  {progress.errors.length > 0 && (
                    <div style={{ textAlign: 'left', marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Errors:</div>
                      {progress.errors.map((e, i) => (
                        <div key={i} style={{ fontSize: '12px', color: 'var(--error)', padding: '4px 0' }}>
                          {e.tenant_id}: {e.error}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          ) : isRunning && progress ? (
            <div aria-live="polite">
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                  <span>Progress</span>
                  <span>{progressPercent}%</span>
                </div>
                <div
                  style={{
                    height: '8px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                  role="progressbar"
                  aria-valuenow={progressPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${progressPercent}%`,
                      background: 'var(--primary)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>

              <div style={{ fontSize: '13px', marginBottom: '12px' }}>
                <div>Completed: {progress.completed} / {progress.total}</div>
                {(progress.skipped || 0) > 0 && (
                  <div style={{ opacity: 0.7 }}>Skipped: {progress.skipped} (already on target version)</div>
                )}
                {progress.failed > 0 && (
                  <div style={{ color: 'var(--error)' }}>Failed: {progress.failed}</div>
                )}
                {progress.in_progress > 0 && <div>In progress: {progress.in_progress}</div>}
                {progress.current_tenant && (
                  <div>
                    Current: {progress.current_tenant}
                    {progress.current_step && (
                      <span style={{ opacity: 0.7 }}> — {progress.current_step} ({progress.current_step_pct || 0}%)</span>
                    )}
                  </div>
                )}
              </div>

              {/* Per-tenant step progress bar */}
              {progress.current_step_pct != null && progress.current_step_pct > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', opacity: 0.6, marginBottom: '4px' }}>
                    {progress.current_step || 'Processing...'}
                  </div>
                  <div style={{ height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${progress.current_step_pct}%`,
                      background: 'var(--accent)',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              )}

              {skippedTenants.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px', opacity: 0.7 }}>Already on target version:</div>
                  {skippedTenants.map((d, i) => (
                    <div
                      key={i}
                      style={{ fontSize: '12px', opacity: 0.6, padding: '4px 0' }}
                    >
                      {d.tenant_slug || d.tenant_id}
                    </div>
                  ))}
                </div>
              )}

              {progress.errors.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Errors:</div>
                  {progress.errors.map((e, i) => (
                    <div
                      key={i}
                      style={{ fontSize: '12px', color: 'var(--error)', padding: '4px 0' }}
                    >
                      {e.tenant_id}: {e.error}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-sm btn-secondary" onClick={onCancel}>
                  Cancel Batch
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                  Target Version
                </label>
                <select
                  value={targetVersion}
                  onChange={(e) => setTargetVersion(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '6px',
                    color: 'inherit',
                  }}
                >
                  <option value="">Select target version</option>
                  {availableVersions.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600 }}>
                    Select Tenants ({selectedTenants.size} of {uniqueTenants.length})
                  </label>
                  <button className="btn btn-sm btn-ghost" onClick={toggleSelectAll}>
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div
                  style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    padding: '4px',
                  }}
                  role="listbox"
                  aria-label="Tenant selection"
                >
                  {uniqueTenants.map((t) => (
                    <label
                      key={t.tenant_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTenants.has(t.tenant_id)}
                        onChange={() => toggleTenant(t.tenant_id)}
                      />
                      <span>{t.tenant_slug}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.6 }}>
                        v{t.version}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                  Parallel Limit: {parallelLimit}
                </label>
                <input
                  type="range"
                  min={1}
                  max={5}
                  value={parallelLimit}
                  onChange={(e) => setParallelLimit(Number(e.target.value))}
                  style={{ width: '100%' }}
                  aria-label="Parallel upgrade limit"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', opacity: 0.5 }}>
                  <span>1</span>
                  <span>5</span>
                </div>
              </div>
            </>
          )}
        </div>

        {!isRunning && (
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleStart}
              disabled={starting || !targetVersion || selectedTenants.size === 0}
            >
              {starting ? 'Starting...' : 'Start Batch Upgrade'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
